// src/pages/VerifyEmail.js - MODERN REDESIGN
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { sendEmailVerification } from "firebase/auth";
import { setDoc, doc, getDoc, serverTimestamp } from "firebase/firestore";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import { normalizeRole, ROLE_VALUE } from "../constants/roleLabels";
import { logger } from "../utils/logger";
import { Mail, Shield } from "lucide-react";

function VerifyEmail() {
  const [resending, setResending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();
  const { setCurrentUserRole, refreshCurrentUser } = useAuth();

  // Send verification email on mount (only if not already sent during signup)
  useEffect(() => {
    const user = auth.currentUser;
    if (user && !user.emailVerified && !emailSent) {
      // Check if email was sent recently (within last 60 seconds)
      const lastSent = localStorage.getItem('verificationEmailSent');
      const now = Date.now();
      
      if (lastSent && (now - parseInt(lastSent)) < 60000) {
        // Email was sent recently during signup, don't send again
        logger.log("Verification email already sent during signup");
        setEmailSent(true);
        return;
      }

      sendEmailVerification(user)
        .then(() => {
          logger.log("Verification email sent");
          setEmailSent(true);
          localStorage.setItem('verificationEmailSent', now.toString());
          toast.info("Verification email sent. Please check your inbox.");
        })
        .catch((error) => {
          console.error("Error sending verification email:", error);
          // Don't show error for rate limit - email was likely already sent
          if (error.code === 'auth/too-many-requests') {
            logger.log("Email verification rate limited - email likely already sent");
            setEmailSent(true);
            // Don't show error toast for rate limit
          } else {
            toast.error("Failed to send verification email. Please try resending.");
          }
        });
    }
  }, [emailSent]);

  // Poll for email verification
  useEffect(() => {
    const checkVerification = setInterval(async () => {
      if (auth.currentUser) {
        await auth.currentUser.reload(); // refresh user data
        if (auth.currentUser.emailVerified) {
          clearInterval(checkVerification);
          await refreshCurrentUser();
          
          // Clean up verification timestamp
          localStorage.removeItem('verificationEmailSent');

          // Ensure Firestore user document is updated with verified flag
          let resolvedRole =
            normalizeRole(localStorage.getItem("userRole")) ||
            ROLE_VALUE.requester;
          try {
            const user = auth.currentUser;
            const userDocRef = doc(db, 'users', user.uid);
            const userDocSnap = await getDoc(userDocRef);

            if (userDocSnap.exists()) {
              const data = userDocSnap.data();
              if (data.role) {
                resolvedRole =
                  normalizeRole(data.role) || ROLE_VALUE.requester;
              }

              await setDoc(
                userDocRef,
                {
                  emailVerified: true,
                  verifiedAt: serverTimestamp(),
                  updatedAt: serverTimestamp(),
                },
                { merge: true }
              );
            } else {
              // Create a minimal profile so login flow always succeeds
              await setDoc(
                userDocRef,
                {
                  uid: user.uid,
                  name: user.displayName || "New User",
                  email: user.email || "",
                  role: resolvedRole,
                  emailVerified: true,
                  createdAt: serverTimestamp(),
                  verifiedAt: serverTimestamp(),
                  updatedAt: serverTimestamp(),
                },
                { merge: true }
              );
            }

            localStorage.setItem("userRole", resolvedRole);
            setCurrentUserRole(resolvedRole);
          } catch (error) {
            console.error('❌ Error updating Firestore user document:', error);
            toast.error('Error completing registration. Please try logging in.');
            return;
          }

          toast.success("Email verified! Redirecting...");

          // Redirect user to their dashboard
          const roleToUse =
            normalizeRole(localStorage.getItem("userRole")) ||
            ROLE_VALUE.requester;
          navigate(
            roleToUse === ROLE_VALUE.specialist
              ? "/dashboard-helper"
              : "/dashboard-client"
          );
        }
      }
    }, 3000); // check every 3s

    return () => clearInterval(checkVerification);
  }, [navigate, refreshCurrentUser, setCurrentUserRole]);

  const handleResend = async () => {
    if (!auth.currentUser) return;

    setResending(true);
    try {
      await sendEmailVerification(auth.currentUser);
      localStorage.setItem('verificationEmailSent', Date.now().toString());
      toast.info("Verification email resent. Please check your inbox.");
      setEmailSent(true);
    } catch (error) {
      console.error("Resend error:", error);
      if (error.code === 'auth/too-many-requests') {
        toast.error("Too many requests. Please wait a few minutes before resending.");
      } else {
        toast.error("Failed to resend verification email. Please try again later.");
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Verify your email
          </h1>
          <p className="text-slate-500 text-sm">
            We've sent a verification link to your email address
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
          <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 border border-blue-100 mb-6">
            <Mail className="w-7 h-7 text-blue-600" />
            <div className="absolute inset-0 rounded-full border-2 border-blue-400 animate-ping opacity-20" />
          </div>

          <h2 className="text-xl font-bold text-slate-900 mb-4">
            Check your inbox
          </h2>

          <div className="space-y-3 mb-6">
            <p className="text-slate-600 text-sm">
              We sent a verification link to:
            </p>
            <p className="text-blue-600 font-semibold text-base break-all">
              {auth.currentUser?.email}
            </p>
            <p className="text-slate-500 text-sm">
              Click the link in the email to verify your account and continue to your dashboard.
            </p>
          </div>

          {/* Helpful Tips */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm font-medium text-slate-700 mb-2">
              Didn't receive the email?
            </p>
            <ul className="text-sm text-slate-600 space-y-1 ml-4">
              <li>• Check your spam or junk folder</li>
              <li>• Make sure {auth.currentUser?.email} is correct</li>
              <li>• Wait a few minutes and refresh this page</li>
            </ul>
          </div>

          {/* Resend Button */}
          <button
            onClick={handleResend}
            disabled={resending}
            className="w-full bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {resending ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Sending...
              </>
            ) : (
              "Resend verification email"
            )}
          </button>
        </div>

        {/* Trust Banner */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 mt-6 flex items-start gap-3">
          <Shield className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-slate-900">
              Your account is secure
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Email verification helps us protect your account and ensure you receive important updates.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerifyEmail;
